class Canvas {
	constructor(options) {
		this.options = options;

		this.initGl = this.initGl.bind(this);
		this.createProgram = this.createProgram.bind(this);
		this.createShader = this.createShader.bind(this);
		this.resizeCanvas = this.resizeCanvas.bind(this);
		this.animate = this.animate.bind(this);
		this.render = this.render.bind(this);
		this.handleIntersection = this.handleIntersection.bind(this);

		this.canvas = options.$element;
		this.isPaused = true;
		this.gl = null;
		this.buffer = null;
		this.vertex_shader = null;
		this.fragment_shader = null;
		this.currentProgram = null;
		this.vertex_position = null;
		this.progressLocation = null;
		this.timeLocation = null;
		this.resolutionLocation = null;

		this.parameters = {
			start_time: new Date().getTime(),
			progress: 0,
			time: 0,
			screenWidth: 0,
			screenHeight: 0,
			centerX: 0.5,
			centerY: -0.25
		};

		this.initGl();

		this.observer = new IntersectionObserver(this.handleIntersection, { thresold: 0.0 });
		this.observer.observe(this.canvas);
	}

	destroy() {
		cancelAnimationFrame(this.rafId);
		this.isPaused = true;
	}

	handleIntersection([entry]) {
		if (entry.isIntersecting) {
			this.isPaused = false;
			this.rafId = requestAnimationFrame(this.animate);
		} else {
			this.isPaused = true;
			cancelAnimationFrame(this.rafId);
		}
	}

	initGl() {
		const { vert, frag } = this.options;

		this.gl = this.canvas.getContext('webgl2');

		if (!this.gl) {
			this.gl = this.canvas.getContext('webgl');
		}

		if (!this.gl) {
			throw 'cannot create webgl context';
		}

		// Create Vertex buffer (2 triangles)
		this.buffer = this.gl.createBuffer();
		this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.buffer);
		this.gl.bufferData(
			this.gl.ARRAY_BUFFER,
			new Float32Array([-1.0, -1.0, 1.0, -1.0, -1.0, 1.0, 1.0, -1.0, 1.0, 1.0, -1.0, 1.0]),
			this.gl.STATIC_DRAW
		);

		// Create Program
		this.currentProgram = this.createProgram(vert, frag);
		this.progressLocation = this.gl.getUniformLocation(this.currentProgram, 'progress');
		this.timeLocation = this.gl.getUniformLocation(this.currentProgram, 'time');
		this.resolutionLocation = this.gl.getUniformLocation(this.currentProgram, 'resolution');
		this.centerLocation = this.gl.getUniformLocation(this.currentProgram, 'center');
	}

	createProgram(vertex, fragment) {
		const { gl, createShader } = this;

		const program = gl.createProgram();

		const vs = createShader(vertex, gl.VERTEX_SHADER);
		const fs = createShader(
			'#ifdef GL_ES\nprecision highp float;\n#endif\n\n' + fragment,
			gl.FRAGMENT_SHADER
		);

		if (vs == null || fs == null) return null;

		gl.attachShader(program, vs);
		gl.attachShader(program, fs);

		gl.deleteShader(vs);
		gl.deleteShader(fs);

		gl.linkProgram(program);

		if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
			alert(
				'ERROR:\n' +
					'VALIDATE_STATUS: ' +
					gl.getProgramParameter(program, gl.VALIDATE_STATUS) +
					'\n' +
					'ERROR: ' +
					gl.getError() +
					'\n\n' +
					'- Vertex Shader -\n' +
					vertex +
					'\n\n' +
					'- Fragment Shader -\n' +
					fragment
			);

			return null;
		}

		return program;
	}

	createShader(src, type) {
		const { gl } = this;
		const shader = gl.createShader(type);

		gl.shaderSource(shader, src);
		gl.compileShader(shader);

		if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
			alert(
				(type == gl.VERTEX_SHADER ? 'VERTEX' : 'FRAGMENT') +
					' SHADER:\n' +
					gl.getShaderInfoLog(shader)
			);
			return null;
		}

		return shader;
	}

	resizeCanvas(event) {
		const { canvas, parameters, gl } = this;

		if (canvas.width != canvas.clientWidth || canvas.height != canvas.clientHeight) {
			canvas.width = canvas.clientWidth;
			canvas.height = canvas.clientHeight;

			parameters.screenWidth = canvas.width;
			parameters.screenHeight = canvas.height;

			gl.viewport(0, 0, canvas.width, canvas.height);
		}
	}

	animate() {
		if (this.isPaused) return;

		const { resizeCanvas, render, animate } = this;

		resizeCanvas();
		render();
		this.rafId = requestAnimationFrame(animate);
	}

	render() {
		const {
			currentProgram,
			parameters,
			gl,
			progressLocation,
			timeLocation,
			resolutionLocation,
			centerLocation,
			buffer,
			vertex_position
		} = this;

		if (!currentProgram) return;

		parameters.time = new Date().getTime() - parameters.start_time;

		gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

		// Load program into GPU

		gl.useProgram(currentProgram);

		// Set values to program variables
		gl.uniform1f(progressLocation, parameters.progress);
		gl.uniform1f(timeLocation, parameters.time / 1000);
		gl.uniform2f(resolutionLocation, parameters.screenWidth, parameters.screenHeight);
		gl.uniform2f(centerLocation, parameters.centerX, parameters.centerY);

		// Render geometry

		gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
		gl.vertexAttribPointer(vertex_position, 2, gl.FLOAT, false, 0, 0);
		gl.enableVertexAttribArray(vertex_position);
		gl.drawArrays(gl.TRIANGLES, 0, 6);
		gl.disableVertexAttribArray(vertex_position);
	}
}

export default Canvas;
